import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Footer } from '@/components/Footer';
import { SpotCard } from '@/components/SpotCard';
import { Seo } from '@/components/Seo';
import { UserAvatar } from '@/components/UserAvatar';
import { KaiaAvatar } from '@/components/KaiaAvatar';
import { KaiaReel } from '@/components/KaiaReel';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import {
  Upload, Activity, GitBranch, Map, Share2, Wind, ArrowRight, Check, Sparkles, Play,
  Star, Eye, Gauge, BarChart3, Target, Globe, Users, Quote, ShieldCheck, Zap, HelpCircle,
} from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/28860320/pexels-photo-28860320/free-photo-of-sunny-beach-with-kite-surfers-in-action.jpeg?auto=compress&cs=tinysrgb&w=1920';
const EMPATHY_IMG = 'https://images.pexels.com/photos/15969122/pexels-photo-15969122/free-photo-of-kitesurfers-in-sea.jpeg?auto=compress&cs=tinysrgb&w=1280';
const PRO_IMG = 'https://images.pexels.com/photos/18696579/pexels-photo-18696579/free-photo-of-people-kitesurfing-on-waves.jpeg?auto=compress&cs=tinysrgb&w=1280';

const FEATURE_ICONS = [Upload, Activity, GitBranch, Map, Share2];
const STEP_NUMBERS = ['01', '02', '03'];
const AUTHORITY_ICONS = [Eye, Gauge, BarChart3, Target];
const STAT_ICONS = [Activity, Users, Star, Globe];
const STAT_VALUES = [
  { end: 12480, decimals: 0, suffix: '+' },
  { end: 2400, decimals: 0, suffix: '+' },
  { end: 4.8, decimals: 1, suffix: '/5' },
  { end: 32, decimals: 0, suffix: '+' },
];
// Illustrative score preview for the authority section (clearly representative).
const PREVIEW_SCORES = [
  { key: 'body_position', v: 8 },
  { key: 'kite_control', v: 7 },
  { key: 'board_edge', v: 6 },
  { key: 'timing', v: 8 },
  { key: 'style', v: 7 },
];

const Stars = ({ n = 5 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className="w-3.5 h-3.5 text-[#FFB020]" fill={i <= n ? '#FFB020' : 'none'} />
    ))}
  </span>
);

const CountUp = ({ end, decimals = 0, suffix = '', locale = 'nl-NL', duration = 1500 }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !done.current) {
        done.current = true;
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(end * eased);
          if (p < 1) requestAnimationFrame(step);
          else setVal(end);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);
  const text = new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val);
  return <span ref={ref} data-testid="stat-value">{text}{suffix}</span>;
};

const reveal = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.06 } }),
};

// Lightweight, on-brand mock visuals for the "How it works" steps so the flow
// feels tangible (upload -> AI analyse -> verbeter) without heavy screenshots.
const StepMock = ({ i }) => {
  const { t } = useTranslation();
  const box = 'kite-card p-4 h-40 flex flex-col justify-center overflow-hidden';
  if (i === 0) {
    return (
      <div className={box} data-testid="step-mock-upload">
        <div className="rounded-xl border border-dashed border-[#48CAE4]/40 bg-[#48CAE4]/5 flex flex-col items-center justify-center py-5">
          <Upload className="w-6 h-6 text-[#48CAE4]" />
          <span className="text-xs text-muted-foreground mt-2">{t('landing.stepMock.uploadHint')}</span>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#0A0A0A] border border-white/10 px-2 py-1 text-[11px] font-data text-white/80">
            <Play className="w-3 h-3 text-[#00B4D8]" /> {t('landing.stepMock.uploadFile')}
          </span>
        </div>
      </div>
    );
  }
  if (i === 1) {
    const rows = [
      { key: 'body_position', v: 8 },
      { key: 'kite_control', v: 7 },
      { key: 'timing', v: 6 },
    ];
    return (
      <div className={box} data-testid="step-mock-analyze">
        <div className="flex items-center gap-1.5 text-[11px] text-[#48CAE4] mb-3">
          <Sparkles className="w-3.5 h-3.5" /> {t('landing.stepMock.analyzing')}
        </div>
        <div className="space-y-2.5">
          {rows.map((r, idx) => (
            <div key={r.key}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-white/70">{t(`scoreLabels.${r.key}`)}</span>
                <span className="font-data text-[#48CAE4]">{r.v}/10</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div initial={{ width: 0 }} whileInView={{ width: `${r.v * 10}%` }} viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.12 }} className="h-full rounded-full bg-[#00B4D8]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={box} data-testid="step-mock-improve">
      <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#00B4D8] text-black font-data text-xs font-bold flex items-center justify-center">1</span>
          <span className="text-sm font-medium">{t('landing.stepMock.drillLabel')}</span>
          <Check className="w-4 h-4 text-[#2EE59D] ml-auto" />
        </div>
        <p className="text-xs text-muted-foreground mt-2 pl-8">{t('landing.stepMock.drillText')}</p>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-[#2EE59D] mt-3">
        <Zap className="w-3.5 h-3.5" /> {t('landing.stepMock.improveHint')}
      </div>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2) === 'nl' ? 'nl-NL' : 'en-US';

  useEffect(() => {
    api.get('/spots').then(({ data }) => setSpots(data.slice(0, 3))).catch(() => {});
  }, []);

  const primaryCta = () => navigate(user ? '/analyze' : '/register');

  const features = t('landing.features', { returnObjects: true });
  const steps = t('landing.steps', { returnObjects: true });
  const proPerks = t('landing.proPerks', { returnObjects: true });
  const authorityPoints = t('landing.authorityPoints', { returnObjects: true });
  const testimonials = t('landing.testimonials', { returnObjects: true });
  const featureList = Array.isArray(features) ? features : [];
  const stepList = Array.isArray(steps) ? steps : [];
  const proPerkList = Array.isArray(proPerks) ? proPerks : [];
  const authorityList = Array.isArray(authorityPoints) ? authorityPoints : [];
  const testimonialList = Array.isArray(testimonials) ? testimonials : [];
  const faqs = t('landing.faqs', { returnObjects: true });
  const faqList = Array.isArray(faqs) ? faqs : [];

  // Structured data (SEO + AI answer-engine "GEO" proofing): identify the brand,
  // expose site search, and surface the FAQ as rich results.
  const SITE_URL = 'https://kitecoach.app';
  const landingJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'KITEIQ',
        url: SITE_URL,
        description: t('landing.heroSub'),
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'KITEIQ',
        inLanguage: ['nl', 'en', 'de'],
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/spots?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      ...(faqList.length
        ? [{
            '@type': 'FAQPage',
            '@id': `${SITE_URL}/#faq`,
            mainEntity: faqList.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }]
        : []),
    ],
  };

  const previewOverall = (PREVIEW_SCORES.reduce((p, c) => p + c.v, 0) / PREVIEW_SCORES.length).toFixed(1);

  return (
    <div>
      <Seo
        title="KITEIQ — AI Kitesurf Coach: video-analyse, spots & wind"
        description="KITEIQ is je AI-kitesurfcoach: upload je sessie voor directe video-analyse en feedback, ontdek kitespots met live wind & verwachting, en krijg gratis gear-advies."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/' : undefined}
        jsonLd={landingJsonLd}
      />
      {/* ===== HERO — Reciprocity (gratis) + Scarcity (early access) + Social proof + Authority ===== */}
      <section className="relative min-h-[90vh] flex items-center noise-overlay overflow-hidden">
        <img src={HERO} alt="Kitesurfer in action" className="absolute inset-0 w-full h-full object-cover" style={{ animation: 'kenburns 22s ease-in-out infinite alternate' }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full">
          <div className="grid lg:grid-cols-[1fr_340px] gap-10 lg:gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={reveal} className="max-w-2xl">
            {/* Scarcity / early-access pill (honest) */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#FF6B35]/40 bg-[#FF6B35]/10 px-3 py-1.5 mb-5" data-testid="landing-announce">
              <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
              <span className="label-eyebrow text-[#FF6B35]">{t('landing.announce')}</span>
            </div>

            <div className="flex items-center gap-2 label-eyebrow text-[#48CAE4] mb-4">
              <Wind className="w-4 h-4" /> {t('landing.eyebrow')}
              <span className="text-white/30">·</span>
              <span className="inline-flex items-center gap-1 text-white/70"><ShieldCheck className="w-3.5 h-3.5 text-[#48CAE4]" /> {t('landing.poweredBy')}</span>
            </div>

            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl">
              {t('landing.heroTitle1')}<br /><span className="text-gradient-blue">{t('landing.heroTitle2')}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/80 mt-5 max-w-xl">{t('landing.heroSub')}</p>

            {/* Concrete promise — what you get, fast */}
            <div className="mt-5 inline-flex items-center gap-2.5 rounded-xl border border-[#48CAE4]/30 bg-[#48CAE4]/8 px-4 py-2.5" data-testid="landing-hero-promise">
              <Zap className="w-4 h-4 text-[#48CAE4] shrink-0" />
              <span className="text-sm sm:text-base font-medium text-white/90">{t('landing.heroPromise')}</span>
            </div>

            {/* Social proof inline */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6" data-testid="landing-hero-socialproof">
              <span className="flex items-center gap-2"><Stars n={5} /> <span className="text-sm text-white/80">{t('landing.ratingText')}</span></span>
              <span className="flex items-center gap-2 text-sm text-white/80"><Users className="w-4 h-4 text-[#48CAE4]" /> {t('landing.ridersText')}</span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Button onClick={primaryCta} data-testid="landing-hero-analyze-cta"
                className="h-12 px-7 text-base bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">
                {t('landing.analyzeCta')} <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              <Button onClick={() => navigate('/spots')} variant="outline"
                className="h-12 px-6 text-base border-white/25 bg-white/5 hover:bg-white/10 text-white rounded-xl" data-testid="landing-explore-spots">
                <Map className="w-5 h-5 mr-1" /> {t('landing.exploreSpots')}
              </Button>
              <Button onClick={() => navigate('/voorbeeld-analyse')} variant="ghost"
                className="h-12 px-5 text-base text-white/85 hover:text-white hover:bg-white/10 rounded-xl" data-testid="landing-view-example">
                <Play className="w-5 h-5 mr-1.5 text-[#48CAE4]" /> {t('landing.viewExample')}
              </Button>
            </div>

            {/* Reciprocity / risk-reversal trust row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-7 text-sm text-white/70">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2EE59D]" /> {t('landing.trust2')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2EE59D]" /> {t('landing.trust1')}</span>
              <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#48CAE4]" /> {t('landing.trust3')}</span>
            </div>
          </motion.div>

          {/* Kaia — the face of the AI coach, prominently on the hero */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="justify-self-center lg:justify-self-end w-[230px] sm:w-[270px] lg:w-[340px] mt-4 lg:mt-0"
          >
            <KaiaReel />
          </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF — stat bar ===== */}
      <section className="border-b border-white/10 bg-[#0E1114]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
          <div className="text-center mb-8">
            <div className="label-eyebrow text-[#48CAE4]">{t('landing.statsEyebrow')}</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">{t('landing.statsTitle')}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-stats">
            {STAT_VALUES.map((s, i) => {
              const Icon = STAT_ICONS[i];
              const labels = [t('landing.statSessions'), t('landing.statRiders'), t('landing.statRating'), t('landing.statCountries')];
              return (
                <motion.div key={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="kite-card p-5 text-center">
                  <Icon className="w-5 h-5 text-[#00B4D8] mx-auto" />
                  <div className="font-data text-4xl font-semibold text-white mt-3">
                    <CountUp end={s.end} decimals={s.decimals} suffix={s.suffix} locale={locale} />
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{labels[i]}</div>
                </motion.div>
              );
            })}
          </div>
          <p className="text-center text-xs text-muted-foreground/70 mt-5">{t('landing.statsNote')}</p>
        </div>
      </section>

      {/* ===== AUTHORITY — why you can trust it ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-[#48CAE4] mb-2">{t('landing.authorityEyebrow')}</div>
            <h2 className="font-display text-4xl sm:text-5xl">{t('landing.authorityTitle')}</h2>
            <p className="text-muted-foreground mt-4 max-w-lg">{t('landing.authorityDesc')}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {authorityList.map((p, i) => {
                const Icon = AUTHORITY_ICONS[i] || Eye;
                return (
                  <motion.div key={p.title} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#00B4D8]" /></div>
                    <div>
                      <h3 className="font-semibold text-sm">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Illustrative report preview card — clickable -> full example */}
          <motion.div
            variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
            onClick={() => navigate('/voorbeeld-analyse')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/voorbeeld-analyse'); } }}
            role="button" tabIndex={0}
            className="kite-card p-6 glow-blue cursor-pointer transition-colors hover:border-[#48CAE4]/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48CAE4]/50"
            data-testid="landing-authority-preview"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 label-eyebrow text-muted-foreground"><BarChart3 className="w-4 h-4 text-[#48CAE4]" /> {t('analysis.techniqueScores')}</div>
              <div className="text-right">
                <span className="font-data text-3xl font-semibold text-[#00B4D8]">{previewOverall}</span>
                <span className="text-sm text-muted-foreground">/10</span>
              </div>
            </div>
            <div className="space-y-3 mt-5">
              {PREVIEW_SCORES.map((s) => (
                <div key={s.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/80">{t(`scoreLabels.${s.key}`)}</span>
                    <span className="font-data text-[#48CAE4]">{s.v}/10</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${s.v * 10}%` }} viewport={{ once: true }} transition={{ duration: 0.8 }}
                      className="h-full rounded-full bg-[#00B4D8]" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-3.5 h-3.5 text-[#48CAE4]" /> {t('landing.poweredBy')}</span>
              <span className="flex items-center gap-1 text-[#48CAE4] font-medium" data-testid="landing-preview-cta-hint">{t('landing.previewCtaHint')} <ArrowRight className="w-3.5 h-3.5" /></span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== LIKING / EMPATHY ===== */}
      <section className="border-y border-white/10 bg-[#0E1114]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-2xl overflow-hidden h-72 lg:h-96 order-2 lg:order-1">
            <img src={EMPATHY_IMG} alt="Kitesurfers" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.6))' }} />
          </div>
          <div className="order-1 lg:order-2">
            <div className="label-eyebrow text-[#48CAE4] mb-2">{t('landing.empathyEyebrow')}</div>
            <h2 className="font-display text-4xl sm:text-5xl">{t('landing.empathyTitle')}</h2>
            <p className="text-muted-foreground mt-4 max-w-lg">{t('landing.empathyDesc')}</p>
            <blockquote className="mt-6 border-l-2 border-[#00B4D8] pl-4 text-white/85 italic">
              {t('landing.empathyQuote')}
            </blockquote>
          </div>
        </div>
      </section>

      {/* ===== FEATURES BENTO ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-2xl">
          <div className="label-eyebrow text-[#48CAE4] mb-2">{t('landing.toolkit')}</div>
          <h2 className="font-display text-4xl sm:text-5xl">{t('landing.everything')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          {featureList.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Upload;
            return (
              <motion.div key={f.title} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className={`kite-card p-6 ${i === 0 ? 'md:col-span-2' : ''} ${i === 3 ? 'md:col-span-2' : ''}`}>
                <div className="w-11 h-11 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#00B4D8]" />
                </div>
                <h3 className="text-lg font-semibold mt-4">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== HOW IT WORKS — commitment ladder ===== */}
      <section className="border-y border-white/10 bg-[#0E1114]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <h2 className="font-display text-4xl sm:text-5xl">{t('landing.howItWorks')}</h2>
          <p className="text-muted-foreground mt-3">{t('landing.howItWorksSub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {stepList.map((s, i) => (
              <motion.div key={s.title} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="relative">
                <StepMock i={i} />
                <div className="flex items-baseline gap-3 mt-5">
                  <div className="font-display text-5xl text-[#00B4D8]/25 leading-none">{STEP_NUMBERS[i]}</div>
                  <h3 className="text-xl font-semibold">{s.title}</h3>
                </div>
                <p className="text-muted-foreground mt-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <Button onClick={primaryCta} className="mt-10 h-12 px-7 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="landing-how-cta">
            {t('landing.analyzeCta')} <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </section>

      {/* ===== FAQ — objection handling ===== */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20" data-testid="landing-faq">
        <div className="text-center mb-8">
          <div className="label-eyebrow text-[#48CAE4] flex items-center justify-center gap-1.5"><HelpCircle className="w-4 h-4" /> {t('landing.faqEyebrow')}</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-2">{t('landing.faqTitle')}</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqList.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="kite-card px-5 border-0" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ===== TESTIMONIALS — social proof + unity ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-eyebrow text-[#48CAE4]">{t('landing.testimonialsEyebrow')}</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-2">{t('landing.testimonialsTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="landing-testimonials">
          {testimonialList.map((r, i) => (
            <motion.figure key={r.name} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
              className="kite-card p-6 flex flex-col" data-testid="testimonial-card">
              <Quote className="w-6 h-6 text-[#00B4D8]/40" />
              <blockquote className="text-sm text-white/90 mt-3 flex-1">“{r.quote}”</blockquote>
              <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/10">
                <UserAvatar src={null} name={r.name} size={40} />
                <div className="min-w-0">
                  <figcaption className="font-medium text-sm truncate">{r.name}</figcaption>
                  <div className="text-xs text-muted-foreground truncate">{r.location}</div>
                </div>
                <div className="ml-auto"><Stars n={r.rating} /></div>
              </div>
            </motion.figure>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground/70 mt-6">{t('landing.testimonialsNote')}</p>
      </section>

      {/* ===== SPOTS TEASER (free value / reciprocity) ===== */}
      <section className="border-t border-white/10 bg-[#0E1114]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="label-eyebrow text-[#48CAE4] mb-2">{t('landing.findWind')}</div>
              <h2 className="font-display text-4xl sm:text-5xl">{t('landing.legendarySpots')}</h2>
            </div>
            <Link to="/spots" className="hidden sm:flex items-center gap-1 text-[#48CAE4] hover:text-[#00B4D8] text-sm font-medium">
              {t('landing.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {spots.map((s) => <SpotCard key={s.id} spot={s} />)}
          </div>
        </div>
      </section>

      {/* ===== UNITY / COMMUNITY ===== */}
      <section className="relative noise-overlay overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 120% at 50% 0%, rgba(0,180,216,0.16), rgba(0,180,216,0) 60%), #0A0A0A' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-20 text-center">
          <div className="flex justify-center -space-x-2 mb-6">
            {['Lars', 'Sofie', 'Tom', 'Niels', 'Emma'].map((n) => (
              <span key={n} className="ring-2 ring-[#0A0A0A] rounded-full"><UserAvatar src={null} name={n} size={44} /></span>
            ))}
          </div>
          <div className="label-eyebrow text-[#48CAE4]">{t('landing.unityEyebrow')}</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-2">{t('landing.unityTitle')}</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">{t('landing.unityDesc')}</p>
          <Button onClick={primaryCta} className="mt-8 h-12 px-8 text-base bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="landing-unity-cta">
            <Users className="w-5 h-5 mr-2" /> {t('landing.unityCta')}
          </Button>
        </div>
      </section>

      {/* ===== PRICING TEASER (Sand) — reciprocity ===== */}
      <section style={{ background: 'var(--kite-sand)' }} className="text-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="label-eyebrow text-[#FF6B35] mb-2">{t('landing.foundingRiders')}</div>
              <h2 className="font-display text-4xl sm:text-5xl">{t('landing.goPro')}</h2>
              <p className="mt-4 text-black/70 max-w-md">{t('landing.proDesc')}</p>
              <ul className="mt-5 space-y-2">
                {proPerkList.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#FF6B35]" /> {p}</li>
                ))}
              </ul>
              <Button onClick={() => navigate('/pricing')} className="mt-7 h-12 px-7 bg-[#0A0A0A] hover:bg-[#1A2129] text-white font-semibold rounded-xl" data-testid="landing-pricing-cta">
                {t('landing.seePricing')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-72 lg:h-80">
              <img src={PRO_IMG} alt="Kitesurfing" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA — reciprocity + commitment + honest scarcity ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#2EE59D]/40 bg-[#2EE59D]/10 px-3 py-1.5 mb-6" data-testid="landing-cta-pill">
          <Sparkles className="w-3.5 h-3.5 text-[#2EE59D]" />
          <span className="label-eyebrow text-[#2EE59D]">{t('landing.ctaPill')}</span>
        </div>
        <h2 className="font-display text-4xl sm:text-6xl">{t('landing.ctaTitle')}</h2>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto">{t('landing.ctaSub')}</p>
        <Button onClick={primaryCta} className="mt-7 h-12 px-8 text-base bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="landing-bottom-cta">
          <Play className="w-5 h-5 mr-1" /> {t('landing.getStartedFree')}
        </Button>
        <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
          <Check className="w-3.5 h-3.5 text-[#2EE59D]" /> {t('landing.ctaTrust')}
        </p>
      </section>

      <Footer />
      <style>{`@keyframes kenburns { 0% { transform: scale(1.05); } 100% { transform: scale(1.16); } }`}</style>
    </div>
  );
}
